import {
  ApiError,
  createConnection,
  deleteConnectionOnUnload,
  GetConnectionResponse,
  updateConnection,
} from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import { AttentionIcon } from '@schooly/style';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

const CONNECTION_RETRY_INTERVAL = 30000;
const CONNECTION_RETRY_NOTIFICATION_ID = 'CONNECTION_RETRY_NOTIFICATION_ID';

type TokenResolver = (token: string) => void;

type UseConnectionProviderProps = {
  schoolId: string;
};

type UseConnectionProviderReturn = {
  tokenProvider: () => Promise<string>;
  hasConnection: boolean;
};

export const useConnectionProvider = ({
  schoolId,
}: UseConnectionProviderProps): UseConnectionProviderReturn => {
  const { showError, showNotification, dismissNotification } = useNotifications();
  const { formatMessage } = useIntl();
  const connection = useRef<GetConnectionResponse | null>(null);
  const interval = useRef<NodeJS.Timeout | null>(null);
  const tokenResolver = useRef<TokenResolver | null>(null);
  const [hasConnection, setHasConnection] = useState(true);

  const getConnection = useCallback(async (): Promise<GetConnectionResponse> => {
    return !connection.current
      ? await createConnection({ school_id: schoolId })
      : await updateConnection({
          school_id: schoolId,
          connection_id: connection.current.connection_id,
        });
  }, [schoolId]);

  const stopRetry = useCallback(() => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }
    setHasConnection(true);
    dismissNotification(CONNECTION_RETRY_NOTIFICATION_ID);
  }, [dismissNotification]);

  const handleConnectionError = useCallback(
    (error: ApiError) => {
      if (error.status_code === 429) {
        setHasConnection(false);

        showNotification({
          id: CONNECTION_RETRY_NOTIFICATION_ID,
          message: formatMessage({ id: 'messages-ConnectionLimitReached' }),
          type: 'error',
          persistent: true,
          icon: <AttentionIcon />,
        });

        interval.current = setInterval(async () => {
          try {
            const connectionData = await getConnection();
            connection.current = connectionData;
            stopRetry();

            if (tokenResolver.current) {
              tokenResolver.current(connectionData.token);
              tokenResolver.current = null;
            }
          } catch (retryError) {
            const err = retryError as ApiError;
            if (err.status_code !== 429) {
              stopRetry();
              showError(err);

              if (tokenResolver.current) {
                tokenResolver.current('');
                tokenResolver.current = null;
              }
            }
          }
        }, CONNECTION_RETRY_INTERVAL);

        return true;
      }

      showError(error);
      return false;
    },
    [getConnection, stopRetry, showNotification, formatMessage, showError],
  );

  const tokenProvider = useCallback(async (): Promise<string> => {
    try {
      const connectionInfo = await getConnection();
      connection.current = connectionInfo;
      return connectionInfo.token;
    } catch (error) {
      const err = error as ApiError;
      const shouldRetry = handleConnectionError(err);

      return shouldRetry
        ? new Promise<string>((resolve) => {
            tokenResolver.current = resolve;
          })
        : '';
    }
  }, [getConnection, handleConnectionError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    const cleanup = () => {
      if (connection.current) {
        deleteConnectionOnUnload({
          school_id: schoolId,
          connection_id: connection.current.connection_id,
        });
      }
    };
    window.addEventListener('beforeunload', cleanup);

    return () => {
      window.removeEventListener('beforeunload', cleanup);
      stopRetry();
      cleanup();
    };
  }, [schoolId]);

  return {
    tokenProvider,
    hasConnection,
  };
};
